import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock, Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { type ViewMode } from "~/hooks/useViewMode";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { useCanDeleteAgent } from "~/pages/manifold-nextgen/hooks/useCanDeleteAgent";
import { useCanDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useCanDuplicateAgent";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, AgentScope, deleteAgent, mutateAgentList } from "~/services/agents";
import { AgentAccessBadge } from "./AgentAccessBadge";
import { agentAvatarSeed, AgentAvatar, resolveAgentAvatarColor } from "./AgentAvatar";
import { DeleteAgentDialog } from "./DeleteAgentDialog";
import { StatusBadge } from "./StatusBadge";

interface AgentCardProps {
  agent: Agent;
  view?: ViewMode;
  favorite?: boolean;
  // When provided, renders a favorite toggle. Omit to hide the affordance entirely.
  onToggleFavorite?: () => void;
}

// A non-favorite toggle stays hidden until the row/card is hovered or the
// button is focused; a favorited one is always visible.
const favoriteVisibility = (favorite: boolean) =>
  !favorite && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

interface AgentActionsProps {
  agent: Agent;
  handleDuplicate: () => void;
  setDeleteOpen: (open: boolean) => void;
}

const AgentActions = ({ agent, handleDuplicate, setDeleteOpen }: AgentActionsProps) => {
  const canDelete = useCanDeleteAgent(agent);
  const canDuplicate = useCanDuplicateAgent(agent);
  // Both items are permission-gated, so a caller holding neither would otherwise
  // get a trigger that opens an empty menu.
  if (!canDuplicate && !canDelete) return null;
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "size-7 shrink-0 text-muted-foreground hover:bg-black/10 hover:text-foreground dark:hover:bg-white/10",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
              "focus-visible:ring-0 focus-visible:border-transparent"
            )}
            aria-label={`Actions for ${agent.display_name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canDuplicate && (
            <DropdownMenuItem onClick={handleDuplicate}>
              <Copy />
              Duplicate
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const AgentCard = ({
  agent,
  view = "card",
  favorite = false,
  onToggleFavorite,
}: AgentCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const duplicateAgentAction = useDuplicateAgent();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const description = useMemo(
    () => (agent.description ? stripMarkdownToText(agent.description) : ""),
    [agent.description]
  );
  const avatarSeed = agentAvatarSeed(agent);
  const avatarAccent = useMemo(
    () => resolveAgentAvatarColor(avatarSeed, agent.avatar_color),
    [avatarSeed, agent.avatar_color]
  );
  const avatarRingStyle = { boxShadow: `0 0 0 2px ${avatarAccent}4D` };
  const handleDuplicate = () => duplicateAgentAction(agent.id, agent.display_name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutateAgentList();
      showSuccess(`"${agent.display_name}" deleted`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge status={agent.status} />
        <AgentAccessBadge accessLevel={agent.access_level} hideDefault />
        {agent.scope === AgentScope.SYSTEM && (
          <Badge variant="outline" className="rounded-full">
            System
          </Badge>
        )}
      </div>
      <p className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <Clock className="size-3.5" aria-hidden="true" />
        {formatDistanceToNowStrict(new Date(agent.updated_at), { addSuffix: true })}
      </p>
    </div>
  );

  if (view === "list") {
    return (
      <>
        <div className="group relative">
          <div
            className={cn(
              "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
              "cursor-pointer transition-colors duration-150 group-hover:bg-muted"
            )}
            onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
          >
            <span className="inline-flex shrink-0 rounded-full" style={avatarRingStyle}>
              <AgentAvatar agent={agent} size={36} />
            </span>
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-foreground">
                {agent.display_name}
              </span>
              <p
                className={cn(
                  "truncate text-xs text-muted-foreground",
                  !description && "italic text-muted-foreground/60"
                )}
              >
                {description || "No description"}
              </p>
            </div>
            <div className="flex w-40 shrink-0 items-center gap-1.5">
              <StatusBadge status={agent.status} />
              <AgentAccessBadge accessLevel={agent.access_level} hideDefault />
              {agent.scope === AgentScope.SYSTEM && (
                <Badge variant="outline" className="rounded-full">
                  System
                </Badge>
              )}
            </div>
            <p className="flex w-36 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
              <Clock className="size-3.5" aria-hidden="true" />
              {formatDistanceToNowStrict(new Date(agent.updated_at), { addSuffix: true })}
            </p>
            {onToggleFavorite && (
              <FavoriteButton
                favorite={favorite}
                onToggle={onToggleFavorite}
                className={cn("shrink-0", favoriteVisibility(favorite))}
              />
            )}
            <AgentActions
              agent={agent}
              handleDuplicate={handleDuplicate}
              setDeleteOpen={setDeleteOpen}
            />
          </div>
        </div>
        <DeleteAgentDialog
          agent={agent}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      </>
    );
  }

  return (
    <>
      <div className="group relative h-full transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]">
        <Card
          className={cn(
            "h-full gap-0 overflow-hidden p-0",
            "cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
          )}
          onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
        >
          <div className="flex flex-1 flex-col gap-3 px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex shrink-0 rounded-full" style={avatarRingStyle}>
                <AgentAvatar agent={agent} size={44} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="block text-base font-semibold leading-tight text-foreground">
                  {agent.display_name}
                </span>
              </div>
              {onToggleFavorite && (
                <FavoriteButton
                  favorite={favorite}
                  onToggle={onToggleFavorite}
                  className={cn("shrink-0", favoriteVisibility(favorite))}
                />
              )}
              <AgentActions
                agent={agent}
                handleDuplicate={handleDuplicate}
                setDeleteOpen={setDeleteOpen}
              />
            </div>

            <p
              className={cn(
                "flex-1 text-sm text-muted-foreground",
                !description && "italic text-muted-foreground/60"
              )}
            >
              {description || "No description"}
            </p>
            {footer}
          </div>
        </Card>

        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 rounded-xl",
            "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
            "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
          )}
        />
      </div>

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
